import { Badge } from '@/components/ui/badge';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { CircleScore } from '@/contracts/circleScore';

interface CircleScoreBadgeProps {
  score: CircleScore;
  className?: string;
  compact?: boolean;
}

export const CircleScoreBadge = ({ score, className = '', compact = false }: CircleScoreBadgeProps) => {
  if (!score.circle_score) return null;

  const scoreColor = 
    score.circle_score >= 8.5 ? 'bg-emerald-600 hover:bg-emerald-700 text-white' :
    score.circle_score >= 7.0 ? 'bg-blue-600 hover:bg-blue-700 text-white' :
    score.circle_score >= 5.5 ? 'bg-amber-500 hover:bg-amber-600 text-white' :
    'bg-muted hover:bg-muted text-muted-foreground';

  if (compact) {
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <Badge className={`${scoreColor} font-bold cursor-help ${className}`}>
              {score.circle_score.toFixed(1)}
            </Badge>
          </TooltipTrigger>
          <TooltipContent side="bottom" className="max-w-xs">
            <div className="space-y-2">
              <p className="font-semibold text-sm">Circle Verified Score™</p>
              <div className="space-y-1 text-xs">
                <div className="flex justify-between gap-4">
                  <span className="text-muted-foreground">Popularity:</span>
                  <span className="font-medium">{score.score_popularity?.toFixed(1) || 'N/A'}/10</span>
                </div>
                <div className="flex justify-between gap-4">
                  <span className="text-muted-foreground">Value:</span>
                  <span className="font-medium">{score.score_value?.toFixed(1) || 'N/A'}/10</span>
                </div>
                <div className="flex justify-between gap-4">
                  <span className="text-muted-foreground">Ease of Use:</span>
                  <span className="font-medium">{score.score_ease_of_use?.toFixed(1) || 'N/A'}/10</span>
                </div>
                <div className="flex justify-between gap-4">
                  <span className="text-muted-foreground">Support:</span>
                  <span className="font-medium">{score.score_support?.toFixed(1) || 'N/A'}/10</span>
                </div>
                <div className="flex justify-between gap-4">
                  <span className="text-muted-foreground">ROI:</span>
                  <span className="font-medium">{score.score_roi?.toFixed(1) || 'N/A'}/10</span>
                </div>
              </div>
              <p className="text-xs text-muted-foreground pt-1 border-t">
                Based on verified agent activity, pricing analysis, and product features
              </p>
            </div>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  // Full display mode
  return (
    <div className={`space-y-2 ${className}`}>
      <div className="flex items-center gap-2">
        <Badge className={`${scoreColor} font-bold text-lg px-3 py-1`}>
          {score.circle_score.toFixed(1)}/10
        </Badge>
        <span className="text-sm font-semibold text-foreground">Circle Verified Score™</span>
      </div>
      
      <div className="grid grid-cols-2 gap-2 text-sm">
        <div>
          <span className="text-muted-foreground">Popularity:</span>
          <span className="ml-2 font-medium">{score.score_popularity?.toFixed(1) || 'N/A'}/10</span>
        </div>
        <div>
          <span className="text-muted-foreground">Value:</span>
          <span className="ml-2 font-medium">{score.score_value?.toFixed(1) || 'N/A'}/10</span>
        </div>
        <div>
          <span className="text-muted-foreground">Ease of Use:</span>
          <span className="ml-2 font-medium">{score.score_ease_of_use?.toFixed(1) || 'N/A'}/10</span>
        </div>
        <div>
          <span className="text-muted-foreground">Support:</span>
          <span className="ml-2 font-medium">{score.score_support?.toFixed(1) || 'N/A'}/10</span>
        </div>
        <div>
          <span className="text-muted-foreground">ROI:</span>
          <span className="ml-2 font-medium">{score.score_roi?.toFixed(1) || 'N/A'}/10</span>
        </div>
      </div>
      
      <p className="text-xs text-muted-foreground">
        Based on verified agent activity, pricing analysis, and product features
      </p>
    </div>
  );
};
